import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Loader2, LogOut, Send } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { ChatWidget } from "@/components/ChatWidget";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function BlockedScreen() {
  const { user, logout, refresh } = useAuth();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const alreadyRequested = !!user.unfreeze_request;

  const submit = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/user/unfreeze-request", { message: msg.trim() });
      toast.success(data.message);
      await refresh();
      setMsg("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex flex-col justify-center px-6 relative overflow-hidden bg-[#0B0E14]">
      <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full bg-[#FF3B30]/10 blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md mx-auto"
      >
        <div className="w-16 h-16 rounded-2xl bg-[#3A141A] flex items-center justify-center mx-auto mb-5">
          <Lock className="text-[#FF3B30]" size={28} />
        </div>
        <h1 className="font-display text-2xl font-bold text-center mb-2">Account Frozen</h1>

        <div
          data-testid="block-message"
          className="rounded-2xl border border-[#FF3B30]/30 bg-[#121721] p-4 text-sm text-slate-200 text-center mb-6"
        >
          {user.block_message || "Your account has been frozen by the administrator."}
        </div>

        {alreadyRequested ? (
          <div className="rounded-xl border border-[#1E293B] bg-[#121721] p-4 text-center">
            <p className="text-sm text-[#00E676] font-semibold mb-1">Unfreeze request sent</p>
            <p className="text-xs text-slate-400">"{user.unfreeze_request}"</p>
            <p className="text-[11px] text-slate-500 mt-2">The admin will review your request soon.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Request to unfreeze your account:</p>
            <Textarea
              data-testid="unfreeze-message-input"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Explain why your account should be unfrozen…"
              className="bg-[#121721] border-[#1E293B] text-white min-h-24"
            />
            <Button
              data-testid="unfreeze-submit-btn"
              onClick={submit}
              disabled={busy}
              className="w-full h-12 bg-[#00E676] hover:bg-[#00C853] text-black font-bold rounded-xl"
            >
              {busy ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} className="mr-2" /> Send Request</>}
            </Button>
          </div>
        )}

        <button
          data-testid="blocked-logout-btn"
          onClick={logout}
          className="w-full mt-6 text-slate-400 text-sm flex items-center justify-center gap-2"
        >
          <LogOut size={16} /> Sign out
        </button>
        <p className="text-center text-[11px] text-slate-600 mt-4">
          You can also reach us via the live support chat.
        </p>
      </motion.div>
      <ChatWidget />
    </div>
  );
}
